import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Building2,
  Clock,
  Facebook,
  Instagram,
  Mail,
  Phone,
  Save,
  ShieldCheck,
} from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { API, toast } from '@/App';

const DEFAULT_DELIVERY_PRICING = {
  local: {
    favor_distance_km: 5,
    favor_weight_kg: 1,
    favor_cost_fcfa: 500,
    favor_cost_usd: 1,
    favor_weight_tolerance_kg: 1.5,
    favor_tolerance_cost_fcfa: 750,
    favor_tolerance_cost_usd: 1.5,
    base_distance_km: 10,
    base_weight_kg: 2,
    base_cost_fcfa: 1000,
    base_cost_usd: 2,
    extra_distance_step_km: 5,
    extra_distance_cost_fcfa: 250,
    extra_distance_cost_usd: 0.5,
    extra_weight_step_kg: 2,
    extra_weight_cost_fcfa: 250,
    extra_weight_cost_usd: 0.5,
  },
  intercity: {
    transfer_base_weight_kg: 2,
    transfer_base_cost_fcfa: 2000,
    transfer_base_cost_usd: 4,
    transfer_extra_weight_step_kg: 1,
    transfer_extra_weight_cost_fcfa: 1250,
    transfer_extra_weight_cost_usd: 2,
    local_distribution_base_distance_km: 15,
    local_distribution_base_weight_kg: 1.5,
    local_distribution_base_cost_fcfa: 750,
    local_distribution_base_cost_usd: 1.5,
    local_distribution_extra_distance_step_km: 5,
    local_distribution_extra_distance_cost_fcfa: 500,
    local_distribution_extra_distance_cost_usd: 1,
    local_distribution_extra_weight_step_kg: 1.5,
    local_distribution_extra_weight_cost_fcfa: 250,
    local_distribution_extra_weight_cost_usd: 0.5,
  },
  special_cases: {
    pnr_kin_multiplier: 2,
    pnr_kin_extra_relay_fee_fcfa: 1000,
    pnr_kin_extra_relay_fee_usd: 2,
    pnr_kin_extra_relay_max_weight_kg: 5,
  },
  general: {
    usd_enabled: true,
    city_currency_map: {
      BZV: 'FCFA',
      PNR: 'FCFA',
      KIN: 'USD',
    },
    country_currency_map: {
      CG: 'FCFA',
      CD: 'USD',
    },
  },
};

const DEFAULT_PAYMENT_METHOD_FLAGS = {
  stripe: false,
  mobile_money: true,
  airtel: false,
  mtn: false,
  orange: false,
  mpesa: false,
  manual: false,
};

const DEFAULT_COMPANY_SETTINGS = {
  company_name: '',
  address: '',
  phone: '',
  email: '',
  niu: '',
  rccm: '',
};

const DEFAULT_PLATFORM_SETTINGS = {
  slogan: '',
  hours: {
    monday_friday: '',
    saturday: '',
    sunday: '',
  },
  social_links: {
    facebook: '',
    instagram: '',
    tiktok: '',
  },
  payment_numbers: {
    airtel: '',
    mtn: '',
    orange: '',
    mpesa: '',
  },
  payment_methods: DEFAULT_PAYMENT_METHOD_FLAGS,
  delivery_pricing: DEFAULT_DELIVERY_PRICING,
};

const mergeDeliveryPricing = (pricing = {}) => ({
  ...DEFAULT_DELIVERY_PRICING,
  ...pricing,
  local: {
    ...DEFAULT_DELIVERY_PRICING.local,
    ...(pricing.local || {}),
  },
  intercity: {
    ...DEFAULT_DELIVERY_PRICING.intercity,
    ...(pricing.intercity || {}),
  },
  special_cases: {
    ...DEFAULT_DELIVERY_PRICING.special_cases,
    ...(pricing.special_cases || {}),
  },
  general: {
    ...DEFAULT_DELIVERY_PRICING.general,
    ...(pricing.general || {}),
    city_currency_map: {
      ...DEFAULT_DELIVERY_PRICING.general.city_currency_map,
      ...(pricing.general?.city_currency_map || {}),
    },
    country_currency_map: {
      ...DEFAULT_DELIVERY_PRICING.general.country_currency_map,
      ...(pricing.general?.country_currency_map || {}),
    },
  },
});

const normalizePlatformSettings = (payload = {}) => ({
  slogan: payload.slogan || '',
  hours: {
    ...DEFAULT_PLATFORM_SETTINGS.hours,
    ...(payload.hours || {}),
  },
  social_links: {
    ...DEFAULT_PLATFORM_SETTINGS.social_links,
    ...(payload.social_links || {}),
  },
  payment_numbers: {
    ...DEFAULT_PLATFORM_SETTINGS.payment_numbers,
    ...(payload.payment_numbers || {}),
  },
  payment_methods: {
    ...DEFAULT_PAYMENT_METHOD_FLAGS,
    ...(payload.payment_methods || {}),
  },
  delivery_pricing: mergeDeliveryPricing(payload.delivery_pricing),
});

const normalizeCompanySettings = (payload = {}) => ({
  company_name: payload.company_name || '',
  address: payload.address || '',
  phone: payload.phone || '',
  email: payload.email || '',
  niu: payload.niu || '',
  rccm: payload.rccm || '',
});

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  fallback;

const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [companySettings, setCompanySettings] = useState(DEFAULT_COMPANY_SETTINGS);
  const [settings, setSettings] = useState(DEFAULT_PLATFORM_SETTINGS);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [authResponse, settingsResponse] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/admin/settings`),
      ]);
      const currentAdmin = authResponse.data || {};
      setIsSuperAdmin(Boolean(currentAdmin.is_super_admin));
      setSettings(normalizePlatformSettings(settingsResponse.data || {}));

      if (currentAdmin.is_super_admin) {
        try {
          const companyResponse = await axios.get(`${API}/admin/company-settings`);
          setCompanySettings(normalizeCompanySettings(companyResponse.data || {}));
        } catch (error) {
          toast.error(extractErrorMessage(error, 'Erreur lors du chargement des paramètres société'));
          setCompanySettings(normalizeCompanySettings(settingsResponse.data || {}));
        }
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erreur lors du chargement'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (field, value) => {
    const normalizedValue = field === 'niu' || field === 'rccm' ? value.toUpperCase() : value;
    setCompanySettings((current) => ({
      ...current,
      [field]: normalizedValue,
    }));
  };

  const handleChange = (field, value) => {
    setSettings((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setSettings((current) => ({
      ...current,
      [parent]: {
        ...current[parent],
        [field]: value,
      },
    }));
  };

  const handlePricingChange = (section, field, value) => {
    const numericValue = value === '' ? '' : Number(value);
    setSettings((current) => ({
      ...current,
      delivery_pricing: {
        ...current.delivery_pricing,
        [section]: {
          ...current.delivery_pricing[section],
          [field]: numericValue,
        },
      },
    }));
  };

  const handlePricingGeneralChange = (field, value) => {
    setSettings((current) => ({
      ...current,
      delivery_pricing: {
        ...current.delivery_pricing,
        general: {
          ...current.delivery_pricing.general,
          [field]: value,
        },
      },
    }));
  };

  const handleCurrencyMapChange = (mapName, field, value) => {
    setSettings((current) => ({
      ...current,
      delivery_pricing: {
        ...current.delivery_pricing,
        general: {
          ...current.delivery_pricing.general,
          [mapName]: {
            ...current.delivery_pricing.general[mapName],
            [field]: value.toUpperCase(),
          },
        },
      },
    }));
  };

  const handleSaveCompany = async (event) => {
    event.preventDefault();
    if (!isSuperAdmin) {
      toast.error('Acces reserve au super admin.');
      return;
    }

    setSavingCompany(true);
    try {
      const response = await axios.patch(`${API}/admin/company-settings`, companySettings);
      setCompanySettings(normalizeCompanySettings(response?.data?.settings || companySettings));
      toast.success('Paramètres société enregistrés avec succès');
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erreur lors de l'enregistrement des paramètres société"));
    } finally {
      setSavingCompany(false);
    }
  };

  const handleSavePlatform = async (event) => {
    event.preventDefault();
    setSavingPlatform(true);

    try {
      await axios.patch(`${API}/admin/settings`, settings);
      toast.success('Paramètres plateforme enregistrés avec succès');
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erreur lors de l'enregistrement"));
    } finally {
      setSavingPlatform(false);
    }
  };

  const renderPricingInput = (label, section, field, step = '0.1') => (
    <div>
      <Label>{label}</Label>
      <Input
        type="number"
        step={step}
        value={settings.delivery_pricing?.[section]?.[field] ?? ''}
        onChange={(event) => handlePricingChange(section, field, event.target.value)}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isSuperAdmin ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Building2 className="h-6 w-6 text-red-600" />
              Paramètres société
            </CardTitle>
            <CardDescription>
              Source de vérité utilisée ensuite pour les exports PDF, rapports, relevés et futures factures.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveCompany} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="company_settings_company_name">Nom de la société</Label>
                  <Input
                    id="company_settings_company_name"
                    value={companySettings.company_name}
                    onChange={(event) => handleCompanyChange('company_name', event.target.value)}
                    placeholder="Coli Noki Sarl"
                  />
                </div>
                <div>
                  <Label htmlFor="company_settings_phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Téléphone principal
                  </Label>
                  <Input
                    id="company_settings_phone"
                    value={companySettings.phone}
                    onChange={(event) => handleCompanyChange('phone', event.target.value)}
                    placeholder="+242 05 087 12 50"
                  />
                </div>
                <div>
                  <Label htmlFor="company_settings_email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </Label>
                  <Input
                    id="company_settings_email"
                    type="email"
                    value={companySettings.email}
                    onChange={(event) => handleCompanyChange('email', event.target.value)}
                    placeholder="contact@colinoki.com"
                  />
                </div>
                <div>
                  <Label htmlFor="company_settings_niu">NIU</Label>
                  <Input
                    id="company_settings_niu"
                    value={companySettings.niu}
                    onChange={(event) => handleCompanyChange('niu', event.target.value)}
                    placeholder="Ex. M2400000000000A"
                  />
                </div>
                <div>
                  <Label htmlFor="company_settings_rccm">RCCM</Label>
                  <Input
                    id="company_settings_rccm"
                    value={companySettings.rccm}
                    onChange={(event) => handleCompanyChange('rccm', event.target.value)}
                    placeholder="Ex. CG-BZV-01-2026-B12-00001"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="company_settings_address">Adresse complète</Label>
                <Textarea
                  id="company_settings_address"
                  rows={3}
                  value={companySettings.address}
                  onChange={(event) => handleCompanyChange('address', event.target.value)}
                  placeholder="Adresse utilisée dans les exports et documents officiels"
                />
              </div>

              <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4 text-sm text-emerald-900">
                Ces champs restent optionnels, mais ils sont désormais centralisés pour tous les documents métier.
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  data-testid="save-company-settings-btn"
                  className="bg-red-500 hover:bg-red-600"
                  disabled={savingCompany}
                >
                  {savingCompany ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Enregistrer les paramètres société
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <ShieldCheck className="h-5 w-5 text-amber-600" />
              Paramètres société
            </CardTitle>
            <CardDescription>Cette section est réservée au super admin.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Les informations légales société, dont le NIU et le RCCM, sont gérées séparément pour rester la source de vérité des exports.
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Paramètres plateforme</CardTitle>
          <CardDescription>Réglages opérationnels réutilisés par le web, les paiements et la tarification.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSavePlatform} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="platform_settings_slogan">Slogan</Label>
                <Input
                  id="platform_settings_slogan"
                  value={settings.slogan}
                  onChange={(event) => handleChange('slogan', event.target.value)}
                  placeholder="Rapide. Fiable. Sans stress."
                />
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                <Clock className="h-5 w-5 text-cyan-600" />
                Horaires d'ouverture
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label>Lundi - Vendredi</Label>
                  <Input
                    value={settings.hours.monday_friday}
                    onChange={(event) => handleNestedChange('hours', 'monday_friday', event.target.value)}
                    placeholder="8h-16h"
                  />
                </div>
                <div>
                  <Label>Samedi</Label>
                  <Input
                    value={settings.hours.saturday}
                    onChange={(event) => handleNestedChange('hours', 'saturday', event.target.value)}
                    placeholder="8h-12h"
                  />
                </div>
                <div>
                  <Label>Dimanche</Label>
                  <Input
                    value={settings.hours.sunday}
                    onChange={(event) => handleNestedChange('hours', 'sunday', event.target.value)}
                    placeholder="fermé"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="mb-4 text-lg font-semibold">Réseaux sociaux</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label className="flex items-center gap-2">
                    <Facebook className="h-4 w-4 text-blue-600" />
                    Facebook
                  </Label>
                  <Input
                    value={settings.social_links.facebook}
                    onChange={(event) => handleNestedChange('social_links', 'facebook', event.target.value)}
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Instagram className="h-4 w-4 text-pink-600" />
                    Instagram
                  </Label>
                  <Input
                    value={settings.social_links.instagram}
                    onChange={(event) => handleNestedChange('social_links', 'instagram', event.target.value)}
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <FaTiktok className="h-4 w-4" />
                    TikTok
                  </Label>
                  <Input
                    value={settings.social_links.tiktok}
                    onChange={(event) => handleNestedChange('social_links', 'tiktok', event.target.value)}
                    placeholder="https://tiktok.com/@..."
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="mb-4 text-lg font-semibold">Numéros Mobile Money</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>Airtel Money</Label>
                  <Input
                    value={settings.payment_numbers.airtel}
                    onChange={(event) => handleNestedChange('payment_numbers', 'airtel', event.target.value)}
                    placeholder="+242 05 087 12 50"
                  />
                </div>
                <div>
                  <Label>MTN Money</Label>
                  <Input
                    value={settings.payment_numbers.mtn}
                    onChange={(event) => handleNestedChange('payment_numbers', 'mtn', event.target.value)}
                    placeholder="+242 06 476 45 82"
                  />
                </div>
                <div>
                  <Label>Orange Money</Label>
                  <Input
                    value={settings.payment_numbers.orange}
                    onChange={(event) => handleNestedChange('payment_numbers', 'orange', event.target.value)}
                    placeholder="Non disponible"
                  />
                </div>
                <div>
                  <Label>M-Pesa</Label>
                  <Input
                    value={settings.payment_numbers.mpesa}
                    onChange={(event) => handleNestedChange('payment_numbers', 'mpesa', event.target.value)}
                    placeholder="Non disponible"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Activation des méthodes de paiement</h3>
                <p className="text-sm text-gray-600">
                  Activez ou désactivez explicitement les moyens de paiement Mobile Money affichés côté vendeur.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {[
                  { key: 'mobile_money', label: 'Mobile Money', description: 'Interrupteur global pour les opérateurs Mobile Money.' },
                  { key: 'airtel', label: 'Airtel Money', description: 'Affiche Airtel Money si activé.' },
                  { key: 'mtn', label: 'MTN Mobile Money', description: 'Affiche MTN Mobile Money si activé.' },
                  { key: 'orange', label: 'Orange Money', description: 'Affiche Orange Money si activé.' },
                  { key: 'mpesa', label: 'M-Pesa', description: 'Affiche M-Pesa si activé.' },
                ].map((item) => (
                  <div key={item.key} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-900">{item.label}</p>
                        <p className="mt-1 text-sm text-gray-500">{item.description}</p>
                      </div>
                      <Switch
                        checked={Boolean(settings.payment_methods?.[item.key])}
                        onCheckedChange={(checked) => handleNestedChange('payment_methods', item.key, checked)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6 border-t pt-6">
              <div>
                <h3 className="mb-1 text-lg font-semibold">Tarification Livraison</h3>
                <p className="text-sm text-gray-600">
                  Ajustez les règles de calcul utilisées par l&apos;estimation et la création des livraisons.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-red-600">Paramètres locaux</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {renderPricingInput('Distance faveur (km)', 'local', 'favor_distance_km')}
                  {renderPricingInput('Poids faveur (kg)', 'local', 'favor_weight_kg')}
                  {renderPricingInput('Poids faveur max (kg)', 'local', 'favor_weight_tolerance_kg')}
                  {renderPricingInput('Cout faveur FCFA', 'local', 'favor_cost_fcfa', '1')}
                  {renderPricingInput('Cout faveur USD', 'local', 'favor_cost_usd')}
                  {renderPricingInput('Cout faveur 1-1.5kg FCFA', 'local', 'favor_tolerance_cost_fcfa', '1')}
                  {renderPricingInput('Cout faveur 1-1.5kg USD', 'local', 'favor_tolerance_cost_usd')}
                  {renderPricingInput('Distance base (km)', 'local', 'base_distance_km')}
                  {renderPricingInput('Poids base (kg)', 'local', 'base_weight_kg')}
                  {renderPricingInput('Cout base FCFA', 'local', 'base_cost_fcfa', '1')}
                  {renderPricingInput('Cout base USD', 'local', 'base_cost_usd')}
                  {renderPricingInput('Step distance sup. (km)', 'local', 'extra_distance_step_km')}
                  {renderPricingInput('Sup distance FCFA', 'local', 'extra_distance_cost_fcfa', '1')}
                  {renderPricingInput('Sup distance USD', 'local', 'extra_distance_cost_usd')}
                  {renderPricingInput('Step poids sup. (kg)', 'local', 'extra_weight_step_kg')}
                  {renderPricingInput('Sup poids FCFA', 'local', 'extra_weight_cost_fcfa', '1')}
                  {renderPricingInput('Sup poids USD', 'local', 'extra_weight_cost_usd')}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-cyan-700">Paramètres inter-villes</h4>
                <p className="text-xs text-gray-600">
                  Règle actuelle : les suppléments de distance locale et de poids local sont appliqués par ville
                  (départ + arrivée), donc x2 au total.
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {renderPricingInput('Poids base transfert (kg)', 'intercity', 'transfer_base_weight_kg')}
                  {renderPricingInput('Cout base transfert FCFA', 'intercity', 'transfer_base_cost_fcfa', '1')}
                  {renderPricingInput('Cout base transfert USD', 'intercity', 'transfer_base_cost_usd')}
                  {renderPricingInput('Step poids transfert (kg)', 'intercity', 'transfer_extra_weight_step_kg')}
                  {renderPricingInput('Sup poids transfert FCFA', 'intercity', 'transfer_extra_weight_cost_fcfa', '1')}
                  {renderPricingInput('Sup poids transfert USD', 'intercity', 'transfer_extra_weight_cost_usd')}
                  {renderPricingInput('Distance locale base (km)', 'intercity', 'local_distribution_base_distance_km')}
                  {renderPricingInput('Poids local base (kg)', 'intercity', 'local_distribution_base_weight_kg')}
                  {renderPricingInput('Cout local base FCFA', 'intercity', 'local_distribution_base_cost_fcfa', '1')}
                  {renderPricingInput('Cout local base USD', 'intercity', 'local_distribution_base_cost_usd')}
                  {renderPricingInput('Step distance locale (km)', 'intercity', 'local_distribution_extra_distance_step_km')}
                  {renderPricingInput('Sup distance locale FCFA', 'intercity', 'local_distribution_extra_distance_cost_fcfa', '1')}
                  {renderPricingInput('Sup distance locale USD', 'intercity', 'local_distribution_extra_distance_cost_usd')}
                  {renderPricingInput('Step poids local (kg)', 'intercity', 'local_distribution_extra_weight_step_kg')}
                  {renderPricingInput('Sup poids local FCFA', 'intercity', 'local_distribution_extra_weight_cost_fcfa', '1')}
                  {renderPricingInput('Sup poids local USD', 'intercity', 'local_distribution_extra_weight_cost_usd')}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-amber-700">Cas spéciaux</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {renderPricingInput('Multiplicateur PNR-KIN', 'special_cases', 'pnr_kin_multiplier')}
                  {renderPricingInput('Frais relais PNR-KIN FCFA', 'special_cases', 'pnr_kin_extra_relay_fee_fcfa', '1')}
                  {renderPricingInput('Frais relais PNR-KIN USD', 'special_cases', 'pnr_kin_extra_relay_fee_usd')}
                  {renderPricingInput('Poids max frais relais (kg)', 'special_cases', 'pnr_kin_extra_relay_max_weight_kg')}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold">Paramètres généraux</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex items-center gap-3 rounded-md border px-3 py-2">
                    <input
                      id="usd-enabled"
                      type="checkbox"
                      checked={Boolean(settings.delivery_pricing?.general?.usd_enabled)}
                      onChange={(event) => handlePricingGeneralChange('usd_enabled', event.target.checked)}
                    />
                    <Label htmlFor="usd-enabled">Activer le mode USD</Label>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <Label>Devise ville BZV</Label>
                    <Input
                      value={settings.delivery_pricing?.general?.city_currency_map?.BZV || ''}
                      onChange={(event) => handleCurrencyMapChange('city_currency_map', 'BZV', event.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Devise ville PNR</Label>
                    <Input
                      value={settings.delivery_pricing?.general?.city_currency_map?.PNR || ''}
                      onChange={(event) => handleCurrencyMapChange('city_currency_map', 'PNR', event.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Devise ville KIN</Label>
                    <Input
                      value={settings.delivery_pricing?.general?.city_currency_map?.KIN || ''}
                      onChange={(event) => handleCurrencyMapChange('city_currency_map', 'KIN', event.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Devise pays CG</Label>
                    <Input
                      value={settings.delivery_pricing?.general?.country_currency_map?.CG || ''}
                      onChange={(event) => handleCurrencyMapChange('country_currency_map', 'CG', event.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Devise pays CD</Label>
                    <Input
                      value={settings.delivery_pricing?.general?.country_currency_map?.CD || ''}
                      onChange={(event) => handleCurrencyMapChange('country_currency_map', 'CD', event.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-6">
              <Button
                type="submit"
                data-testid="save-settings-btn"
                className="bg-red-500 hover:bg-red-600"
                disabled={savingPlatform}
              >
                {savingPlatform ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Enregistrer les paramètres plateforme
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanySettings;
